import { ContactUs } from "../components/ContactUs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { Plus, Eye, Megaphone, Loader2, Pause, Play, Ban, CreditCard, Trash2, Heart } from "lucide-react";
import { TemplateArt } from "../components/TemplateArt";

export default function Dashboard() {
  const { user } = useAuth();
  const [greetings, setGreetings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/greetings/mine").then(({ data }) => setGreetings(data)),
      api.get("/ads/my-campaigns").then(({ data }) => setCampaigns(data)),
    ]).finally(() => setLoading(false));
  }, []);

  const [busy, setBusy] = useState(null);
  const deleteGreeting = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Remove this greeting from your dashboard? The recipient's copy will still work.")) return;
    setGreetings((gs) => gs.filter((g) => g.id !== id));
    try {
      await api.delete(`/greetings/${id}`);
    } catch {
      // re-fetch on failure to stay in sync
      api.get("/greetings/mine").then(({ data }) => setGreetings(data)).catch(() => {});
    }
  };
  const manageBilling = async () => {
    try {
      const { data } = await api.post("/ads/billing-portal", { origin_url: window.location.origin });
      if (data.available && data.url) {
        window.location.href = data.url;
      } else {
        alert(data.message || "Billing management isn't available yet.");
      }
    } catch (e) {
      alert("Couldn't open billing management right now.");
    }
  };
  const campaignAction = async (id, action) => {
    setBusy(id + action);
    try {
      const { data } = await api.post(`/ads/campaigns/${id}/${action}`);
      setCampaigns((cs) => cs.map((c) => (c.id === id ? { ...c, status: data.status } : c)));
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold">Hey {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-[#4A4A4A] mt-1">Your greetings and campaigns, all in one place.</p>
          </div>
          <Link to="/templates" data-testid="dashboard-create-btn" className="nb-btn bg-[#FFB5A7] px-6 py-3"><Plus className="w-5 h-5" /> New greeting</Link>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <>
            <h2 className="text-xl font-display font-bold mt-10 mb-4">Sent greetings ({greetings.length})</h2>
            {greetings.length === 0 ? (
              <div className="nb-card p-10 text-center">
                <p className="font-semibold">No greetings yet.</p>
                <Link to="/templates" className="nb-btn bg-[#A8E6CF] px-6 py-3 mt-4 inline-flex">Create your first</Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="my-greetings">
                {greetings.map((g) => (
                  <Link key={g.id} to={`/g/${g.share_token}`} className="nb-card p-0 overflow-hidden hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#0A0A0A] transition-all">
                    <div className="h-36 border-b-2 border-[#0A0A0A] relative">
                      {g.template_gradient ? (
                        <TemplateArt gradient={g.template_gradient} category={g.category} title={g.template_title} titleClass="text-lg" />
                      ) : (
                        <img src={resolveImg(g.template_image)} alt={g.template_title} className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={(e) => deleteGreeting(e, g.id)}
                        data-testid={`delete-greeting-${g.id}`}
                        title="Remove from dashboard"
                        className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-lg border-2 border-[#0A0A0A] bg-white/95 hover:bg-[#FFD5CC] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-3">
                      <div className="font-display font-bold truncate">{g.recipient_name || g.template_title}</div>
                      <div className="text-xs text-[#4A4A4A] flex items-center gap-1 mt-1"><Eye className="w-3 h-3" /> {g.views} views</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-12 mb-4">
              <h2 className="text-xl font-display font-bold">Ad campaigns ({campaigns.length})</h2>
              <div className="flex gap-2">
                {campaigns.length > 0 && (
                  <button onClick={manageBilling} data-testid="manage-billing-btn" className="nb-btn bg-white px-4 py-2 text-sm"><CreditCard className="w-4 h-4" /> Manage billing</button>
                )}
                <Link to="/advertise" className="nb-btn bg-white px-4 py-2 text-sm"><Megaphone className="w-4 h-4" /> New campaign</Link>
              </div>
            </div>
            {campaigns.length === 0 ? (
              <div className="nb-card p-8 text-center text-[#4A4A4A]">No ad campaigns yet. <Link to="/advertise" className="font-bold underline">Advertise with us</Link>.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="my-campaigns">
                {campaigns.map((c) => (
                  <div key={c.id} className="nb-card p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold">{c.business_name}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="text-sm text-[#4A4A4A] mt-1">{c.headline}</p>
                    <div className="text-xs font-bold uppercase tracking-widest mt-3">{c.tier} {c.target_city || c.target_state ? `· ${c.target_city || c.target_state}` : ""}</div>
                    <div className="flex gap-4 mt-3 text-sm font-semibold">
                      <span>{c.impressions} views</span><span>{c.clicks} clicks</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {c.status === "active" && (
                        <button onClick={() => campaignAction(c.id, "pause")} disabled={busy === c.id + "pause"} data-testid={`pause-${c.id}`} className="nb-btn bg-[#F9F871] px-3 py-1.5 text-xs">
                          {busy === c.id + "pause" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />} Pause
                        </button>
                      )}
                      {c.status === "paused" && (
                        <button onClick={() => campaignAction(c.id, "resume")} disabled={busy === c.id + "resume"} data-testid={`resume-${c.id}`} className="nb-btn bg-[#A8E6CF] px-3 py-1.5 text-xs">
                          {busy === c.id + "resume" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Resume
                        </button>
                      )}
                      {!["cancelled", "expired"].includes(c.status) && (
                        <button onClick={() => { if (window.confirm("Cancel this campaign? Your ad will stop showing.")) campaignAction(c.id, "cancel"); }} disabled={busy === c.id + "cancel"} data-testid={`cancel-${c.id}`} className="nb-btn bg-white px-3 py-1.5 text-xs">
                          {busy === c.id + "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <footer className="border-t-2 border-[#0A0A0A] py-8 text-center text-sm text-[#4A4A4A]">
        <div className="flex items-center justify-center flex-wrap gap-4 mb-3">
          <Link to="/terms" data-testid="footer-terms" className="font-semibold underline">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="font-semibold underline">Privacy Policy</Link>
          <Link to="/advertise" className="font-semibold underline">Advertise</Link>
          <ContactUs />
        </div>
        SendOccasions — made with <Heart className="w-4 h-4 inline" /> · Sending greetings is always free.
      </footer>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-[#A8E6CF]", pending_payment: "bg-[#F9F871]", paused: "bg-[#FFE08A]",
    payment_failed: "bg-[#FFD5CC]", cancelled: "bg-gray-200", expired: "bg-gray-200",
  };
  return <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-lg border-2 border-[#0A0A0A] ${map[status] || "bg-white"}`}>{(status || "").replace("_", " ")}</span>;
}
